import React, { useCallback, useEffect, useRef, useState } from 'react'

/*
  HOVER-TO-OPEN WITH INTENT — the shared machinery behind every menu in the app that
  opens on hover.

  The naive version (`onMouseEnter` -> open, `onMouseLeave` -> close) is worse than
  click, and every reason is about the POINTER'S PATH rather than either endpoint:

  1. ENTERING A TRIGGER IS NOT INTENT. The sidebar is a VERTICAL rail and its
     menu-bearing rows (Help, the reader status, the profile chip) sit at the BOTTOM,
     so the cursor routinely travels THROUGH them on the way somewhere else. An
     open-on-entry menu fires during ordinary navigation. Hence OPEN_DELAY: the pointer
     has to still be there a beat later for the entry to count as a request.

  2. LEAVING A TRIGGER IS NOT DISMISSAL. These menus open to the SIDE, clearing the
     drawer by a gap (`sidebarMenuPlacement`), so reaching a row means travelling right
     AND up/down — off the trigger, across rows the menu does not belong to, over the
     gap, and only then onto the paper. Every one of those is a `pointerleave` in the
     naive version. Hence CLOSE_DELAY, which is a TRAVEL BUDGET and not a politeness
     delay: it has to outlast the whole diagonal. The trigger and the menu are ONE
     hover region, which is what `surfaceProps` is for.

  3. THE MODAL BACKDROP IS THE TRAP THAT BREAKS THIS SILENTLY. A MUI `Menu` is a
     `Modal`, and its root is a full-viewport element that swallows pointer events.
     The moment the menu opens, that root covers the trigger that opened it — so the
     trigger's hover ends, `pointerleave` fires while the pointer has not moved, and
     the menu closes itself one CLOSE_DELAY later. Every handler here would be correct
     and the feature would still not work.

     So a hover menu's root gets `pointerEvents: 'none'` and its paper
     `pointerEvents: 'auto'` (`HOVER_MENU_ROOT_SX` / `HOVER_MENU_PAPER_SX` below) — the
     same technique MUI's own hover-popover recipe uses.

     That has a consequence this hook then has to pay for: the root no longer catches
     outside clicks or Escape, so those are re-implemented here as document listeners
     (see the effect at the bottom). The visible difference from a modal menu is that
     a click outside now also reaches whatever is underneath rather than being
     swallowed — which is how hover menus behave everywhere, and saves the user the
     dismiss-then-click double click.

  HOVER IS ADDITIVE, NEVER A REPLACEMENT. `handleTriggerClick` still opens AND toggles
  closed (the only path on a touch device, which has no hover at all), Enter/Space
  still open, Escape closes and returns focus to the trigger when it was opened from
  the keyboard.

  FOCUS IS THE OTHER HALF. A menu that opened because the pointer drifted must NOT
  steal focus — that would move the caret for a gesture the user did not make. So a
  consumer always passes `disableAutoFocus` / `disableEnforceFocus` /
  `disableRestoreFocus` (Modal-level) and switches the MenuList's own `autoFocus` on
  `openedByKeyboard`, so the keyboard user lands inside the menu and the pointer user's
  focus stays put.

  TOUCH. Every handler early-returns on a non-mouse `pointerType`, so a tap never
  schedules a hover-open behind the click it already fired.
*/

/**
 * How long the pointer must REST on a sidebar trigger before its menu opens.
 *
 * 200ms, sized against the thing it has to reject: a deliberate pass-through of a 48px
 * sidebar row costs 60-120ms at a normal 400-800px/s, so 200 clears ordinary transit
 * with margin. It also stays under the ~250-300ms where a hover-open stops reading as
 * responsive and starts reading as lag.
 *
 * Deliberately SLOWER than the 130ms the profile menu's Theme submenu uses. That is a
 * different geometry — 44px rows inside a menu the user is already committed to, where
 * transit is faster and the cost of a stray open is lower — which is exactly why the
 * delay is an option rather than a constant baked into the hook.
 */
export const HOVER_INTENT_OPEN_DELAY = 200

/**
 * How long a menu survives with the pointer on neither it nor its trigger.
 *
 * This is the travel budget of note 2 above. The uncovered stretch on a sidebar menu is
 * the 20px gap between the drawer edge and the paper (`SIDEBAR_MENU_GAP`), plus — on a
 * diagonal toward a row near the top of the paper — a brief pass over a neighbouring
 * sidebar row before crossing it: 40-80ms on this layout. 250ms covers that at any
 * plausible pointer speed while being short enough that the menu does not conspicuously
 * linger once you have settled somewhere else.
 */
export const HOVER_INTENT_CLOSE_DELAY = 250

/** Spread onto the Menu's root slot. See note 3. */
export const HOVER_MENU_ROOT_SX = { pointerEvents: 'none' } as const

/** ...and back on for the paper, which does need to be hovered and clicked. */
export const HOVER_MENU_PAPER_SX = { pointerEvents: 'auto' } as const

export interface HoverIntentSurfaceProps {
  onPointerEnter: (event: React.PointerEvent<HTMLElement>) => void
  onPointerLeave: (event: React.PointerEvent<HTMLElement>) => void
}

export interface UseHoverIntentOptions {
  openDelay?: number
  closeDelay?: number
  /**
   * Turns the hover half off entirely, leaving click/keyboard behaviour intact. Lets a
   * shared component (e.g. `ActionDropdown`) call this hook unconditionally — hooks
   * rules — while only opting specific call sites into hover.
   */
  enabled?: boolean
}

export interface UseHoverIntentResult {
  anchorEl: HTMLElement | null
  open: boolean
  /** True when the CURRENT open was caused by Enter/Space/an explicit keyboard path. */
  openedByKeyboard: boolean
  /** Spread onto the trigger element (the same element the menu anchors to). */
  triggerProps: HoverIntentSurfaceProps
  /** Spread onto the menu paper, so trigger + menu count as one hover region. */
  surfaceProps: HoverIntentSurfaceProps
  /** Opens on click, closes on a second click. `detail === 0` detects Enter/Space. */
  handleTriggerClick: (event: React.MouseEvent<HTMLElement>) => void
  openNow: (element: HTMLElement, viaKeyboard?: boolean) => void
  closeNow: () => void
  /** Close, returning focus to the trigger if the menu had it. Wire to `Menu.onClose`. */
  closeAndRestoreFocus: () => void
  /** Start (or restart) the travel budget. Any re-entry cancels it. */
  scheduleClose: () => void
  cancelPendingOpen: () => void
  cancelPendingClose: () => void
}

export const useHoverIntent = ({
  openDelay = HOVER_INTENT_OPEN_DELAY,
  closeDelay = HOVER_INTENT_CLOSE_DELAY,
  enabled = true,
}: UseHoverIntentOptions = {}): UseHoverIntentResult => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [openedByKeyboard, setOpenedByKeyboard] = useState(false)

  /*
    Timers and pointer occupancy as REFS, not state: they are scheduling machinery that
    no render reads, so putting them in state would re-render the whole subtree on every
    pointer twitch across the trigger and every row of the menu.
  */
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * The focus re-assert poll (see `closeAndRestoreFocus`). Typed `number` rather than
   * `ReturnType<typeof setInterval>` because it is created with `window.setInterval`,
   * which is unambiguously the DOM overload — the bare global can resolve to Node's
   * `Timeout` when `@types/node` is in scope.
   */
  const restoreFocusTimerRef = useRef<number | null>(null)
  const overTriggerRef = useRef(false)
  const overSurfaceRef = useRef(false)
  /** What opened it, so Escape can hand focus back after a keyboard open. */
  const triggerRef = useRef<HTMLElement | null>(null)

  const cancelPendingOpen = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const cancelPendingClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const cancelFocusRestore = useCallback(() => {
    if (restoreFocusTimerRef.current !== null) {
      window.clearInterval(restoreFocusTimerRef.current)
      restoreFocusTimerRef.current = null
    }
  }, [])

  // A timer that fires after unmount would setState on a dead component; a timer that
  // survives a close would reopen a menu nobody asked for. Both are the classic bug in
  // this pattern, so both are cleared here and on every open/close below.
  useEffect(
    () => () => {
      cancelPendingOpen()
      cancelPendingClose()
      cancelFocusRestore()
    },
    [cancelPendingOpen, cancelPendingClose, cancelFocusRestore],
  )

  const openNow = useCallback(
    (element: HTMLElement, viaKeyboard = false) => {
      cancelPendingOpen()
      cancelPendingClose()
      triggerRef.current = element
      setOpenedByKeyboard(viaKeyboard)
      setAnchorEl(element)
    },
    [cancelPendingOpen, cancelPendingClose],
  )

  const closeNow = useCallback(() => {
    cancelPendingOpen()
    cancelPendingClose()
    setAnchorEl(null)
    setOpenedByKeyboard(false)
  }, [cancelPendingOpen, cancelPendingClose])

  /**
   * Close, and hand focus back to the trigger if the menu had taken it.
   *
   * Wire this to the Menu's `onClose`, NOT just to a key handler. MUI's Modal calls
   * `stopPropagation()` on Escape before invoking `onClose`, so once focus is inside the
   * menu — i.e. exactly the keyboard case that needs a restore — a document-level
   * keydown listener never sees the event at all. `onClose` is the only callback that
   * fires on every close path the Modal owns.
   */
  const closeAndRestoreFocus = useCallback(() => {
    const shouldRestore = openedByKeyboard
    const trigger = triggerRef.current
    closeNow()
    if (!shouldRestore || !trigger) return
    /*
      RE-ASSERTED, not fired once — measured, not defensive. Focusing synchronously here
      works and is then undone ~100ms later, when the still-mounted menu tears down and
      blurs what it had focused, dropping focus to <body> (sampled: menu gone by ~100ms,
      focus on <body> from then on). A restore performed after that point sticks.

      A fixed delay would bake MUI's current transition duration in as a magic number, so
      this polls and stops the moment it holds. Bounded both ways: success, or ~500ms.
    */
    trigger.focus()
    // Held in a ref, not just the closure, so unmount can clear it. A close that is
    // immediately followed by unmount (navigating away from the menu) would otherwise
    // leave this polling against a detached node for the rest of the 500ms.
    cancelFocusRestore()
    const deadline = Date.now() + 500
    restoreFocusTimerRef.current = window.setInterval(() => {
      if (document.activeElement === trigger || Date.now() > deadline) {
        cancelFocusRestore()
        return
      }
      trigger.focus()
    }, 32)
  }, [closeNow, openedByKeyboard, cancelFocusRestore])

  const scheduleClose = useCallback(() => {
    cancelPendingOpen()
    cancelPendingClose()
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      setAnchorEl(null)
      setOpenedByKeyboard(false)
    }, closeDelay)
  }, [cancelPendingOpen, cancelPendingClose, closeDelay])

  const isMouse = (event: React.PointerEvent<HTMLElement>) => event.pointerType === 'mouse'

  const handleTriggerPointerEnter = (event: React.PointerEvent<HTMLElement>) => {
    if (!enabled || !isMouse(event)) return
    overTriggerRef.current = true
    // Re-entering the trigger is a reprieve whether or not the menu is open yet.
    cancelPendingClose()
    if (anchorEl) return
    const element = event.currentTarget
    cancelPendingOpen()
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null
      // Belt and braces against a leave that raced the timer.
      if (!overTriggerRef.current) return
      triggerRef.current = element
      setOpenedByKeyboard(false)
      setAnchorEl(element)
    }, openDelay)
  }

  const handleTriggerPointerLeave = (event: React.PointerEvent<HTMLElement>) => {
    if (!enabled || !isMouse(event)) return
    overTriggerRef.current = false
    cancelPendingOpen()
    if (anchorEl) scheduleClose()
  }

  const handleSurfacePointerEnter = (event: React.PointerEvent<HTMLElement>) => {
    if (!enabled || !isMouse(event)) return
    overSurfaceRef.current = true
    cancelPendingClose()
  }

  const handleSurfacePointerLeave = (event: React.PointerEvent<HTMLElement>) => {
    if (!enabled || !isMouse(event)) return
    overSurfaceRef.current = false
    if (anchorEl) scheduleClose()
  }

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    cancelPendingOpen()
    cancelPendingClose()
    if (anchorEl) {
      closeNow()
      return
    }
    // `detail === 0` = a click synthesised from Enter/Space rather than a pointer press
    // — the standard way to tell the two apart on one handler, and the same test the
    // profile menu already uses.
    openNow(event.currentTarget, event.detail === 0)
  }

  /*
    Escape and outside-click, re-implemented because `HOVER_MENU_ROOT_SX` takes them away
    from the Modal (note 3). Escape ALSO has to live here for a reason independent of
    that: a hover-opened menu never takes focus, so MUI's own handler — which only sees
    keys dispatched from inside the modal — would never fire.

    Outside-click is decided from this hook's own occupancy bookkeeping rather than from
    node containment, so it needs no ref to the paper and cannot go stale as the menu
    re-renders.
  */
  useEffect(() => {
    if (!anchorEl) return undefined

    /*
      This catches Escape only for a menu that did NOT take focus — a hover-opened one,
      where the keypress goes to whatever the user was actually working in and never
      reaches the Modal. When focus IS inside the menu, MUI's Modal handles Escape and
      stops propagation before this listener could ever run; `closeAndRestoreFocus`,
      wired to the Menu's `onClose`, is what covers that path.
    */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      closeAndRestoreFocus()
    }

    const handleMouseDown = () => {
      if (overTriggerRef.current || overSurfaceRef.current) return
      closeNow()
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [anchorEl, closeNow, closeAndRestoreFocus])

  return {
    anchorEl,
    open: Boolean(anchorEl),
    openedByKeyboard,
    triggerProps: {
      onPointerEnter: handleTriggerPointerEnter,
      onPointerLeave: handleTriggerPointerLeave,
    },
    surfaceProps: {
      onPointerEnter: handleSurfacePointerEnter,
      onPointerLeave: handleSurfacePointerLeave,
    },
    handleTriggerClick,
    openNow,
    closeNow,
    closeAndRestoreFocus,
    scheduleClose,
    cancelPendingOpen,
    cancelPendingClose,
  }
}
